/*
    Copyright 2022-2025, Techwise Integrated Security, LLC
*/

import React, { useState } from "react";
import { Alert, Box, Tooltip, useTheme } from "@mui/material";
import Button from "@mui/material/Button";
import Modal from "@mui/material/Modal";
import Backdrop from "@mui/material/Backdrop";
import Fade from "@mui/material/Fade";
import TextField from "@mui/material/TextField";

import { Company, Effect, ModalBase } from "../../utils/Interfaces";
import { companyNameExists, createCompany } from "./companiesFunctions";
import { selectExistingCompanies } from "./companiesSlice";
import { AnyAction, Dispatch } from "@reduxjs/toolkit";
import { useAppDispatch, useAppSelector } from "../../app/hooks";

export interface CreateCompanyModalProps extends ModalBase {
    onCompanyCreated: Effect;
}

const CreateCompanyModal = (props: CreateCompanyModalProps) => {
    const existingCompanies: Company[] = useAppSelector(selectExistingCompanies);

    const { open, handleClose, onCompanyCreated } = props;

    const [newCompanyName, setNewCompanyName] = useState<string>("");

    const dispatch: Dispatch<AnyAction> = useAppDispatch();

    const theme = useTheme();

    function canSubmitCompany() {
        let processedCompanyName: string = newCompanyName.trim();
        return processedCompanyName !== "" && !companyNameExists(processedCompanyName, existingCompanies);
    }

    function getErrorMessage(): string {
        let processedCompanyName: string = newCompanyName.trim();
        if (newCompanyName === "") {
            return "The company's name cannot be blank.";
        }

        if (companyNameExists(processedCompanyName, existingCompanies)) {
            return `A company with the name "${processedCompanyName}" already exists.`;
        }

        return "";
    }

    return (
        <Box>
            <Modal
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
                open={open}
                onClose={handleClose}
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{
                    timeout: 500,
                }}
            >
                <Fade in={open}>
                    <Box
                        sx={{
                            backgroundColor: theme.palette.background.paper,
                            border: "1px solid #000",
                            boxShadow: theme.shadows[5],
                            padding: theme.spacing(2, 4, 3),
                            zIndex: 1000,
                            width: 600,
                        }}
                    >
                        <Box component="form" noValidate>
                            {getErrorMessage() !== "" && <Alert severity="error">{getErrorMessage()}</Alert>}
                            {getErrorMessage() === "" && <Alert>Company name is available.</Alert>}

                            <TextField
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setNewCompanyName(event.target.value)}
                                margin="normal"
                                required
                                fullWidth
                                id="name"
                                label="name"
                                name="name"
                                defaultValue={""}
                            />
                            <Tooltip title={canSubmitCompany() ? "Create New Company" : "Company Name is Invalid"}>
                                <Button
                                    onClick={(event) => {
                                        createCompany({ name: newCompanyName.trim() }, dispatch, onCompanyCreated);
                                    }}
                                    fullWidth
                                    variant="contained"
                                    sx={{ mt: 3, mb: 2 }}
                                    color="primary"
                                    disabled={!canSubmitCompany()}
                                >
                                    Submit
                                </Button>
                            </Tooltip>
                        </Box>
                    </Box>
                </Fade>
            </Modal>
        </Box>
    );
};

export default CreateCompanyModal;
